<template>
  <component
    :is="withContainer ? 'ChiftContainer' : 'div'"
    v-if="status !== 'DIS'"
    v-bind="withContainer ? { size: 'extra-small' } : {}"
  >
    <!--
      Laid out for a 256px column: the label wraps, the button spans the width and
      the history icon sits on the status line rather than beside the button, so
      the interrupted state never overflows.
    -->
    <div class="flex flex-col gap-1.5 text-xs">
      <div class="flex items-start gap-1.5">
        <FontAwesomeIcon
          :icon="['fas', isInterrupted ? 'triangle-exclamation' : 'circle-check']"
          :class="isInterrupted ? 'mt-0.5 text-amber-500' : 'mt-0.5 text-green-500'"
        />
        <span class="capitalize-first flex-1 font-medium text-gray-700">
          {{ statusLabel }}
        </span>
        <button
          type="button"
          class="shrink-0 text-gray-400 hover:text-gray-600"
          :title="$t('invoicing.payment_sync_invoice_history')"
          @click="showHistory = true"
        >
          <FontAwesomeIcon :icon="['fal', 'clock-rotate-left']" />
        </button>
      </div>

      <template v-if="isInterrupted">
        <BaseButton
          :title="$t('invoicing.payments_sync_reenable')"
          :disabled="!canReenable || loading"
          color="primary"
          size="extra-small"
          class="w-full"
          @click="reenable"
        />
        <p v-if="!canReenable" class="capitalize-first text-amber-700">
          {{ $t('invoicing.payments_sync_reenable_blocked') }}
        </p>
      </template>
    </div>

    <ChiftPaymentSyncRunModal
      v-if="showHistory"
      :invoice-id="invoice.id"
      @close="showHistory = false"
    />
  </component>
</template>

<script>
import { mapActions } from 'vuex'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import ChiftContainer from '@c/Partner/Chift/ChiftContainer.vue'
import ChiftPaymentSyncRunModal from '@c/Partner/Chift/ChiftPaymentSyncRunModal.vue'

export default {
  name: 'InvoicingPaymentSyncBanner',
  components: {
    FontAwesomeIcon,
    BaseButton,
    ChiftContainer,
    ChiftPaymentSyncRunModal
  },
  props: {
    invoice: {
      type: Object,
      required: true
    },
    /**
     * False when rendered inside the accounting block that already provides the
     * Chift container, so the partner frame and logo are not drawn twice.
     */
    withContainer: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      loading: false,
      showHistory: false
    }
  },
  computed: {
    status() {
      return this.invoice.payments_sync_status
    },
    isInterrupted() {
      return this.status === 'INT'
    },
    canReenable() {
      return this.invoice.can_reenable_payments_sync !== false
    },
    statusLabel() {
      if (!this.isInterrupted) {
        return this.$t('invoicing.payments_sync_active')
      }

      return this.$t('invoicing.payments_sync_interrupted_banner', {
        date: this.$h.formatDate(
          this.invoice.payments_sync_changed_at,
          'D MMM YYYY HH:mm',
          this.$i18n.locale.substring(0, 2)
        )
      })
    }
  },
  methods: {
    ...mapActions({
      processEnableInvoicePaymentsSyncAction:
        'invoicing/enableInvoicePaymentsSync',
      processFetchInvoiceByIdAction: 'invoicing/fetchInvoiceID'
    }),
    reenable() {
      if (!this.canReenable) {
        return
      }

      this.loading = true
      this.processEnableInvoicePaymentsSyncAction({
        invoice_id: this.invoice.id
      })
        .then(() => {
          this.processFetchInvoiceByIdAction({ id: this.invoice.id })
        })
        .catch((reason) => {
          this.$h.toastDanger(
            reason.response?.data?.message ?? this.$t('toasts.something_went_wrong')
          )
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>
